import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { 
  ChevronRight, 
  ChevronDown, 
  ChevronLeft,
  X, 
  Plus, 
  Check,
  CheckCheck,
  Info,
  Wrench,
  Eye,
  Clock,
  Bug,
  Zap,
  Target,
  AlertCircle,
  AlertTriangle,
  Settings,
  Link2,
  Database,
  Shield,
  RefreshCw,
  List,
  Search,
  Filter,
  Sparkles,
  ArrowRight,
  SkipForward,
  Keyboard,
  TrendingUp,
  DollarSign,
  Timer,
  CheckSquare,
  Square,
  ChevronUp,
  Play,
  FastForward,
  GitBranch
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { API } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import DiagnosticPathwayPanel from './DiagnosticPathwayPanel';

/**
 * JobCompletenessPanel - Enhanced JCE V3 Rule Engine UI
 * 
 * ENHANCED FEATURES:
 * - One-Click "Apply All Suggestions"
 * - Smart Summary Dashboard
 * - Progress Indicator
 * - Keyboard Shortcuts (A=Add, S=Skip, N=Next)
 * - Auto-Expand Most Important
 * - "Next Unpriced Item" Button
 * - Bulk Actions
 * - Search/Filter
 * - Better Visual Hierarchy
 * - Collapsible Sections
 * - Estimated Time/Cost Preview
 */

const JobCompletenessPanel = ({
  isOpen,
  onClose,
  estimateId,
  jobGroups = [],
  inspectionFindings = [],
  selectedJob = null,
  onSelectJob,
  vehicleContext = {},
  onAddItem,
  onRequestInspect,
  onApplyAllSuggestions,
  isAdmin = false
}) => {
  // Core State
  const [jceData, setJceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [debugMode, setDebugMode] = useState(false);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'list', 'detail'
  
  // Enhanced State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all'); // 'all', 'required', 'often', 'possible'
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    completed: true,
    priced: true
  });
  const [expandedCategories, setExpandedCategories] = useState({
    required: true,
    often: true,
    possible: false
  });
  const [showPathwayPanel, setShowPathwayPanel] = useState(false);

  // Refs for keyboard navigation
  const panelRef = useRef(null);
  const currentItemIndex = useRef(0);

  // Combine all sources for the selector with enhanced metadata
  const allSelectableItems = useMemo(() => {
    const items = [];
    
    // Add inspection findings with priority order
    const diagnosticFindings = inspectionFindings.filter(f => 
      f.item_key?.includes('warning') || f.item_key?.includes('dash_warning')
    );
    const dangerousFindings = inspectionFindings.filter(f => 
      f.status === 'dangerous' && !f.item_key?.includes('warning')
    );
    const redFindings = inspectionFindings.filter(f => 
      f.status === 'red' && !f.item_key?.includes('warning')
    );
    const amberFindings = inspectionFindings.filter(f => f.status === 'amber');
    
    // Process in priority order: Diagnostic > Dangerous > Red > Amber
    [...diagnosticFindings, ...dangerousFindings, ...redFindings, ...amberFindings].forEach((finding, idx) => {
      const isDiagnostic = finding.item_key?.includes('warning') || finding.item_key?.includes('dash_warning');
      // Check if pricing is CONFIRMED (not just that a job group exists)
      const matchingJobGroup = jobGroups.find(jg => 
        jg.vi_item_key === finding.item_key || 
        jg.inspection_item_id === finding.id
      );
      // isPriced = job group exists AND pricing_confirmed is true
      const isPriced = matchingJobGroup?.pricing_confirmed === true;
      
      items.push({
        id: finding.id || finding.item_key,
        source: 'inspection',
        status: isDiagnostic ? 'diagnostic' : finding.status,
        title: finding.item_label || finding.label || finding.item_key,
        item_key: finding.item_key,
        notes: finding.notes,
        section: finding.section,
        isDiagnostic,
        isPriced,
        priority: isDiagnostic ? 0 : finding.status === 'dangerous' ? 1 : finding.status === 'red' ? 2 : 3,
        estimatedLabour: getEstimatedLabour(finding.item_key),
        estimatedParts: getEstimatedParts(finding.item_key)
      });
    });
    
    // Add job groups not from inspection - only show as priced if pricing_confirmed
    jobGroups.forEach(jg => {
      const alreadyListed = items.some(item => 
        item.item_key === jg.vi_item_key || 
        item.id === jg.inspection_item_id
      );
      
      if (!alreadyListed) {
        items.push({
          id: jg.id,
          source: 'estimate',
          status: jg.pricing_confirmed ? 'priced' : 'pending',
          title: jg.job_name || jg.title || 'Unnamed Job',
          job_name: jg.job_name || jg.title,
          inspection_item_key: jg.inspection_item_key,
          isPriced: jg.pricing_confirmed === true,
          priority: 4
        });
      }
    });
    
    return items;
  }, [inspectionFindings, jobGroups]);

  // Calculate summary statistics - TOTAL counts and breakdown
  const summaryStats = useMemo(() => {
    const total = allSelectableItems.length;
    const priced = allSelectableItems.filter(i => i.isPriced).length;
    
    // TOTAL counts (regardless of pricing status)
    const totalDiagnostic = allSelectableItems.filter(i => i.isDiagnostic).length;
    const totalDangerous = allSelectableItems.filter(i => i.status === 'dangerous' && !i.isDiagnostic).length;
    const totalRed = allSelectableItems.filter(i => i.status === 'red' && !i.isDiagnostic).length;
    const totalAmber = allSelectableItems.filter(i => i.status === 'amber').length;
    
    // UNPRICED counts (items still needing attention)
    const unpricedDiagnostic = allSelectableItems.filter(i => i.isDiagnostic && !i.isPriced).length;
    const unpricedDangerous = allSelectableItems.filter(i => i.status === 'dangerous' && !i.isDiagnostic && !i.isPriced).length;
    const unpricedRed = allSelectableItems.filter(i => i.status === 'red' && !i.isDiagnostic && !i.isPriced).length;
    const unpricedAmber = allSelectableItems.filter(i => i.status === 'amber' && !i.isPriced).length;
    
    // Legacy names for compatibility
    const needsAttention = unpricedDiagnostic + unpricedDangerous + unpricedRed;
    const diagnostic = unpricedDiagnostic;
    const dangerous = unpricedDangerous;
    const amber = unpricedAmber;
    
    const completionPercent = total > 0 ? Math.round((priced / total) * 100) : 100;
    
    // Calculate estimated totals for unpriced items
    const estimatedLabourHours = allSelectableItems
      .filter(i => !i.isPriced)
      .reduce((sum, i) => sum + (i.estimatedLabour || 0), 0);
    const estimatedPartsValue = allSelectableItems
      .filter(i => !i.isPriced)
      .reduce((sum, i) => sum + (i.estimatedParts || 0), 0);
    
    return {
      total,
      priced,
      needsAttention,
      amber,
      diagnostic,
      dangerous,
      // NEW: Total counts
      totalDiagnostic,
      totalDangerous,
      totalRed,
      totalAmber,
      // NEW: Unpriced counts
      unpricedDiagnostic,
      unpricedDangerous,
      unpricedRed,
      unpricedAmber,
      completionPercent,
      estimatedLabourHours,
      estimatedPartsValue
    };
  }, [allSelectableItems]);

  // Get next unpriced item
  const nextUnpricedItem = useMemo(() => {
    return allSelectableItems.find(item => !item.isPriced);
  }, [allSelectableItems]);

  // Filtered items based on search and category
  const filteredItems = useMemo(() => {
    let items = allSelectableItems;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.title?.toLowerCase().includes(query) ||
        item.notes?.toLowerCase().includes(query) ||
        item.item_key?.toLowerCase().includes(query)
      );
    }
    
    // Apply category filter
    if (filterCategory !== 'all') {
      if (filterCategory === 'unpriced') {
        items = items.filter(i => !i.isPriced);
      } else if (filterCategory === 'priced') {
        items = items.filter(i => i.isPriced);
      } else if (filterCategory === 'critical') {
        items = items.filter(i => i.status === 'red' || i.status === 'dangerous' || i.status === 'diagnostic');
      }
    }
    
    return items;
  }, [allSelectableItems, searchQuery, filterCategory]);

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Don't capture if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toLowerCase();
      
      switch (key) {
        case 'a':
          // Add current item
          if (selectedJob && !decisions[selectedJob.item_key]) {
            handleDecision(selectedJob, 'include');
            toast.success('Item added (A)');
          }
          break;
        case 's':
          // Skip current item
          if (selectedJob && !decisions[selectedJob.item_key]) {
            handleDecision(selectedJob, 'not_applicable');
            toast.info('Item skipped (S)');
          }
          break;
        case 'n':
          // Next unpriced item
          if (nextUnpricedItem) {
            handleSelectItem(nextUnpricedItem);
            toast.info('Jumped to next item (N)');
          }
          break;
        case 'escape':
          if (viewMode === 'detail') {
            handleBackToList();
          }
          break;
        case '?':
          setShowKeyboardHelp(prev => !prev);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedJob, decisions, nextUnpricedItem, viewMode]);

  // Auto-select first unpriced item when panel opens
  useEffect(() => {
    if (isOpen && viewMode === 'dashboard' && nextUnpricedItem && !selectedJob) {
      // Auto-focus on first critical item after a short delay
      const timer = setTimeout(() => {
        if (summaryStats.needsAttention > 0) {
          const firstCritical = allSelectableItems.find(i => 
            !i.isPriced && (i.status === 'red' || i.status === 'dangerous' || i.status === 'diagnostic')
          );
          if (firstCritical) {
            // Don't auto-select, just highlight in dashboard
          }
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, viewMode]);

  // Build vehicle context for API
  const buildVehicleContext = useCallback(() => {
    return {
      fuel_type: (vehicleContext.fuel_type || 'petrol').toLowerCase(),
      mileage: vehicleContext.mileage || 0,
      age_years: vehicleContext.age_years || (vehicleContext.year ? new Date().getFullYear() - vehicleContext.year : 0),
      is_ev: ['electric', 'ev', 'bev'].includes((vehicleContext.fuel_type || '').toLowerCase()),
      steering_type: vehicleContext.steering_type || 'unknown'
    };
  }, [vehicleContext]);

  // Get job keys already in estimate
  const getEstimateJobKeys = useCallback(() => {
    return jobGroups.map(jg => {
      const title = (jg.job_name || jg.title || '').toLowerCase();
      return title.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    });
  }, [jobGroups]);

  // Get job title from item
  const getJobTitleFromItem = useCallback((item) => {
    if (!item) return '';
    if (item.job_name) return item.job_name;
    if (item.item_key) {
      const itemKeyToJob = {
        'drive_belt_condition': 'Auxiliary Belt Replacement',
        'front_brake_pads': 'Front Brake Pad Replacement',
        'front_brake_discs': 'Front Brake Disc Replacement',
        'front_brake_pads_discs': 'Front Brake Pads & Discs',
        'rear_brake_pads': 'Rear Brake Pad Replacement',
        'rear_brake_discs': 'Rear Brake Disc Replacement',
        'front_springs': 'Front Spring Replacement',
        'front_shock_absorbers': 'Front Shock Absorber Replacement',
        'rear_springs': 'Rear Spring Replacement',
        'rear_shock_absorbers': 'Rear Shock Absorber Replacement',
        'warning_lamps': 'Diagnostic Investigation',
        'dash_warning_lights': 'Diagnostic Investigation',
        'engine_warning_light': 'Engine Diagnostic',
        'abs_warning_light': 'ABS Diagnostic',
        'airbag_warning_light': 'Airbag System Diagnostic'
      };
      
      if (itemKeyToJob[item.item_key]) return itemKeyToJob[item.item_key];
      
      for (const [key, job] of Object.entries(itemKeyToJob)) {
        if (item.item_key.includes(key) || key.includes(item.item_key)) return job;
      }
      
      return item.item_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    return item.title || '';
  }, []);

  // Fetch JCE evaluation
  useEffect(() => {
    const fetchJCE = async () => {
      if (!isOpen || !selectedJob || viewMode !== 'detail') {
        return;
      }

      const jobTitle = getJobTitleFromItem(selectedJob);
      if (!jobTitle) {
        setJceData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(`${API}/jce/v3/evaluate`, {
          job_title: jobTitle,
          vehicle_context: buildVehicleContext(),
          estimate_job_keys: getEstimateJobKeys()
        });

        setJceData(response.data);
      } catch (err) {
        console.error('JCE V3 evaluation failed:', err);
        setError('Unable to load suggestions');
      } finally {
        setLoading(false);
      }
    };

    fetchJCE();
  }, [isOpen, selectedJob?.id, viewMode, buildVehicleContext, getEstimateJobKeys, getJobTitleFromItem]);

  // Handle decision
  const handleDecision = async (item, decisionType) => {
    const itemKey = item.item_key || item.job_key || item.id;
    setDecisions(prev => ({
      ...prev,
      [itemKey]: decisionType
    }));

    if (decisionType === 'include' && onAddItem) {
      // Pass all relevant fields for both inspection findings and suggestions
      onAddItem({
        id: item.id,
        item_key: item.item_key,  // For inspection findings
        job_key: item.job_key || item.item_key,  // For suggestions (fallback to item_key)
        title: item.title || item.name,
        category: item.category,
        reason: item.reason,
        notes: item.notes,
        status: item.status,
        parent_job_id: item.parent_job_id || selectedJob?.id,
        estimatedLabour: item.estimatedLabour,
        estimatedParts: item.estimatedParts,
        source: item.source
      });
    }

    if (decisionType === 'inspect' && onRequestInspect) {
      onRequestInspect({
        id: item.id,
        job_key: item.job_key || item.item_key,
        title: item.name || item.title,
        note: item.reason,
        parent_job_id: item.parent_job_id || selectedJob?.id
      });
    }

    // Record decision to backend
    try {
      await axios.post(`${API}/jce/v3/decision`, {
        estimate_id: estimateId,
        source_job_id: jceData?.source_job?.id || '',
        suggestion_job_key: itemKey,
        decision: decisionType
      });
    } catch (err) {
      console.error('Failed to record decision:', err);
    }
  };

  // Handle Apply All Suggestions
  const handleApplyAll = async () => {
    if (!jceData?.suggestions || jceData.suggestions.length === 0) {
      toast.info('No suggestions to apply');
      return;
    }

    setApplyingAll(true);
    const suggestions = jceData.suggestions.filter(s => !decisions[s.job_key]);
    let addedCount = 0;

    try {
      for (const suggestion of suggestions) {
        await handleDecision(suggestion, 'include');
        addedCount++;
      }
      toast.success(`Added ${addedCount} items to estimate`);
    } catch (err) {
      toast.error('Failed to apply all suggestions');
    } finally {
      setApplyingAll(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedItems.size === 0) {
      toast.info('No items selected');
      return;
    }

    const items = Array.from(selectedItems);
    for (const itemId of items) {
      const item = allSelectableItems.find(i => i.id === itemId);
      if (item) {
        await handleDecision(item, action);
      }
    }
    
    setSelectedItems(new Set());
    toast.success(`${action === 'include' ? 'Added' : 'Skipped'} ${items.length} items`);
  };

  // Handle item selection
  const handleSelectItem = (item) => {
    onSelectJob?.(item);
    setViewMode('detail');
  };

  // Handle back to list/dashboard
  const handleBackToList = () => {
    setViewMode('dashboard');
    onSelectJob?.(null);
    setJceData(null);
  };

  // Toggle item selection for bulk
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Go to next unpriced
  const goToNextUnpriced = () => {
    if (nextUnpricedItem) {
      handleSelectItem(nextUnpricedItem);
    }
  };

  const isDecided = (key) => !!decisions[key];
  const getDecision = (key) => decisions[key];

  if (!isOpen) return null;

  // Parse suggestions
  const suggestions = jceData?.suggestions || [];
  const requiredItems = suggestions.filter(s => s.category === 'REQUIRED');
  const oftenItems = suggestions.filter(s => s.category === 'OFTEN');
  const possibleItems = suggestions.filter(s => s.category === 'POSSIBLE');
  const sourceJob = jceData?.source_job;
  const integrityScore = jceData?.integrity_score;

  // Score colors
  const getScoreColor = (level) => {
    if (level === 'GREEN') return 'bg-green-500';
    if (level === 'AMBER') return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div 
      ref={panelRef}
      className="fixed right-0 top-0 h-full w-[440px] bg-white shadow-2xl z-30 overflow-hidden flex flex-col"
      data-testid="jce-panel"
    >
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 shrink-0">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Job Completeness Engine
              <Badge variant="secondary" className="text-xs bg-emerald-500/20 text-emerald-300">
                Enhanced
              </Badge>
            </h3>
            <p className="text-slate-400 text-xs mt-1">
              {viewMode === 'dashboard' ? 'Smart workflow assistant' : 
               viewMode === 'detail' ? `Viewing: ${getJobTitleFromItem(selectedJob)}` :
               `${allSelectableItems.length} items to review`}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowKeyboardHelp(!showKeyboardHelp)}
              className={`p-1.5 rounded transition-colors ${
                showKeyboardHelp ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title="Keyboard shortcuts (?)"
            >
              <Keyboard className="w-4 h-4" />
            </button>
            {isAdmin && (
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`p-1.5 rounded transition-colors ${
                  debugMode ? 'bg-yellow-500 text-slate-800' : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
                title="Debug mode"
              >
                <Bug className="w-4 h-4" />
              </button>
            )}
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      {showKeyboardHelp && (
        <div className="bg-blue-900 text-blue-100 px-4 py-3 text-xs border-b border-blue-800">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <Keyboard className="w-3 h-3" />
            Keyboard Shortcuts
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><kbd className="bg-blue-800 px-1.5 py-0.5 rounded">A</kbd> Add item</div>
            <div><kbd className="bg-blue-800 px-1.5 py-0.5 rounded">S</kbd> Skip item</div>
            <div><kbd className="bg-blue-800 px-1.5 py-0.5 rounded">N</kbd> Next unpriced</div>
            <div><kbd className="bg-blue-800 px-1.5 py-0.5 rounded">Esc</kbd> Back</div>
          </div>
        </div>
      )}

      {/* DASHBOARD VIEW */}
      {viewMode === 'dashboard' && (
        <>
          {/* Smart Status Header */}
          <div className="p-4 bg-gradient-to-b from-slate-50 to-white border-b">
            {/* Main Status Message */}
            {summaryStats.completionPercent === 100 ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">All Items Priced!</p>
                  <p className="text-sm text-green-600">{summaryStats.priced} items in this estimate</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">
                    {summaryStats.total - summaryStats.priced} Item{summaryStats.total - summaryStats.priced !== 1 ? 's' : ''} Need Pricing
                  </p>
                  <p className="text-sm text-amber-600">{summaryStats.priced} of {summaryStats.total} items priced</p>
                </div>
              </div>
            )}
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-slate-500">Estimate Progress</span>
                <span className="text-sm font-bold text-slate-700">{summaryStats.completionPercent}%</span>
              </div>
              <Progress value={summaryStats.completionPercent} className="h-2" />
            </div>
            
            {/* Inspection Summary Tiles - ALWAYS show totals */}
            <div className="mb-2">
              <p className="text-xs font-medium text-slate-500 mb-2">Inspection Breakdown</p>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {/* Diagnostic Tile */}
              <div 
                className={`rounded-lg p-2 border-2 cursor-pointer transition-all hover:scale-105 ${
                  summaryStats.totalDiagnostic > 0 
                    ? 'bg-purple-50 border-purple-400' 
                    : 'bg-slate-50 border-slate-200'
                }`}
                onClick={() => summaryStats.totalDiagnostic > 0 && setViewMode('list')}
              >
                <div className={`text-xl font-bold ${summaryStats.totalDiagnostic > 0 ? 'text-purple-600' : 'text-slate-400'}`}>
                  {summaryStats.totalDiagnostic}
                </div>
                <div className="text-xs text-slate-500">Diagnostic</div>
                {summaryStats.unpricedDiagnostic > 0 && (
                  <div className="text-xs text-purple-600 font-medium mt-0.5">
                    {summaryStats.unpricedDiagnostic} unpriced
                  </div>
                )}
                {summaryStats.totalDiagnostic > 0 && summaryStats.unpricedDiagnostic === 0 && (
                  <div className="text-xs text-green-600 mt-0.5">✓ Done</div>
                )}
              </div>
              
              {/* Dangerous Tile */}
              <div 
                className={`rounded-lg p-2 border-2 cursor-pointer transition-all hover:scale-105 ${
                  summaryStats.totalDangerous > 0 
                    ? 'bg-black/5 border-black' 
                    : 'bg-slate-50 border-slate-200'
                }`}
                onClick={() => summaryStats.totalDangerous > 0 && setViewMode('list')}
              >
                <div className={`text-xl font-bold ${summaryStats.totalDangerous > 0 ? 'text-black' : 'text-slate-400'}`}>
                  {summaryStats.totalDangerous}
                </div>
                <div className="text-xs text-slate-500">Dangerous</div>
                {summaryStats.unpricedDangerous > 0 && (
                  <div className="text-xs text-black font-medium mt-0.5">
                    {summaryStats.unpricedDangerous} unpriced
                  </div>
                )}
                {summaryStats.totalDangerous > 0 && summaryStats.unpricedDangerous === 0 && (
                  <div className="text-xs text-green-600 mt-0.5">✓ Done</div>
                )}
              </div>
              
              {/* Red Tile */}
              <div 
                className={`rounded-lg p-2 border-2 cursor-pointer transition-all hover:scale-105 ${
                  summaryStats.totalRed > 0 
                    ? 'bg-red-50 border-red-400' 
                    : 'bg-slate-50 border-slate-200'
                }`}
                onClick={() => summaryStats.totalRed > 0 && setViewMode('list')}
              >
                <div className={`text-xl font-bold ${summaryStats.totalRed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                  {summaryStats.totalRed}
                </div>
                <div className="text-xs text-slate-500">Red</div>
                {summaryStats.unpricedRed > 0 && (
                  <div className="text-xs text-red-600 font-medium mt-0.5">
                    {summaryStats.unpricedRed} unpriced
                  </div>
                )}
                {summaryStats.totalRed > 0 && summaryStats.unpricedRed === 0 && (
                  <div className="text-xs text-green-600 mt-0.5">✓ Done</div>
                )}
              </div>
              
              {/* Amber Tile */}
              <div 
                className={`rounded-lg p-2 border-2 cursor-pointer transition-all hover:scale-105 ${
                  summaryStats.totalAmber > 0 
                    ? 'bg-amber-50 border-amber-400' 
                    : 'bg-slate-50 border-slate-200'
                }`}
                onClick={() => summaryStats.totalAmber > 0 && setViewMode('list')}
              >
                <div className={`text-xl font-bold ${summaryStats.totalAmber > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                  {summaryStats.totalAmber}
                </div>
                <div className="text-xs text-slate-500">Amber</div>
                {summaryStats.unpricedAmber > 0 && (
                  <div className="text-xs text-amber-600 font-medium mt-0.5">
                    {summaryStats.unpricedAmber} unpriced
                  </div>
                )}
                {summaryStats.totalAmber > 0 && summaryStats.unpricedAmber === 0 && (
                  <div className="text-xs text-green-600 mt-0.5">✓ Done</div>
                )}
              </div>
            </div>

            {/* Estimated Value Preview */}
            {summaryStats.estimatedLabourHours > 0 && (
              <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    Potential Value (unpriced)
                  </span>
                  <span className="font-semibold text-green-800">
                    ~{summaryStats.estimatedLabourHours}h labour + £{summaryStats.estimatedPartsValue} parts
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="p-3 bg-slate-100 border-b flex gap-2">
            <Button
              size="sm"
              className={`flex-1 ${nextUnpricedItem ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
              onClick={goToNextUnpriced}
              disabled={!nextUnpricedItem}
            >
              <FastForward className="w-4 h-4 mr-1" />
              {nextUnpricedItem ? 'Next Unpriced' : 'All Priced ✓'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4 mr-1" />
              View All ({allSelectableItems.length})
            </Button>
          </div>

          {/* Priority Items Preview */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Critical Items First */}
            {summaryStats.needsAttention > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-red-700 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Needs Attention ({summaryStats.needsAttention})
                  </h4>
                </div>
                <div className="space-y-2">
                  {allSelectableItems
                    .filter(i => !i.isPriced && (i.status === 'red' || i.status === 'dangerous' || i.status === 'diagnostic'))
                    .slice(0, 5)
                    .map((item, idx) => (
                      <QuickItemCard
                        key={item.id}
                        item={item}
                        onClick={() => handleSelectItem(item)}
                        onQuickAdd={() => handleDecision(item, 'include')}
                      />
                    ))}
                  {summaryStats.needsAttention > 5 && (
                    <button
                      className="w-full text-sm text-red-600 hover:text-red-800 py-2"
                      onClick={() => setViewMode('list')}
                    >
                      +{summaryStats.needsAttention - 5} more items...
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Amber Items */}
            {summaryStats.amber > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Advisory ({summaryStats.amber})
                  </h4>
                  <button
                    className="text-xs text-amber-600 hover:text-amber-800"
                    onClick={() => {
                      setFilterCategory('amber');
                      setViewMode('list');
                    }}
                  >
                    View all
                  </button>
                </div>
                <div className="space-y-2">
                  {allSelectableItems
                    .filter(i => !i.isPriced && i.status === 'amber')
                    .slice(0, 3)
                    .map((item, idx) => (
                      <QuickItemCard
                        key={item.id}
                        item={item}
                        onClick={() => handleSelectItem(item)}
                        onQuickAdd={() => handleDecision(item, 'include')}
                      />
                    ))}
                </div>
              </div>
            )}

            {/* All Complete */}
            {summaryStats.needsAttention === 0 && summaryStats.amber === 0 && (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <h4 className="font-semibold text-green-800 mb-2">All Items Priced!</h4>
                
                {/* Show priced items summary */}
                <div className="bg-slate-50 rounded-lg p-3 text-left">
                  <p className="text-xs font-medium text-slate-500 mb-2">Estimate includes:</p>
                  <div className="space-y-1">
                    {summaryStats.totalDiagnostic > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-purple-700 flex items-center gap-1">
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          Diagnostic
                        </span>
                        <span className="font-medium">{summaryStats.totalDiagnostic}</span>
                      </div>
                    )}
                    {summaryStats.totalDangerous > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-black flex items-center gap-1">
                          <span className="w-2 h-2 bg-black rounded-full"></span>
                          Dangerous
                        </span>
                        <span className="font-medium">{summaryStats.totalDangerous}</span>
                      </div>
                    )}
                    {summaryStats.totalRed > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-red-700 flex items-center gap-1">
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          Red (Urgent)
                        </span>
                        <span className="font-medium">{summaryStats.totalRed}</span>
                      </div>
                    )}
                    {summaryStats.totalAmber > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-amber-700 flex items-center gap-1">
                          <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                          Amber (Advisory)
                        </span>
                        <span className="font-medium">{summaryStats.totalAmber}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between text-sm font-medium">
                      <span className="text-slate-700">Total Items</span>
                      <span className="text-green-700">{summaryStats.priced}</span>
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => setViewMode('list')}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Review All Items
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <>
          {/* Search & Filter Bar */}
          <div className="p-3 bg-slate-50 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {['all', 'unpriced', 'critical', 'priced'].map(cat => (
                <Button
                  key={cat}
                  size="sm"
                  variant={filterCategory === cat ? 'default' : 'ghost'}
                  className="h-7 text-xs capitalize"
                  onClick={() => setFilterCategory(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>
            
            {/* Bulk Actions */}
            {selectedItems.size > 0 && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <span className="text-xs text-slate-500">{selectedItems.size} selected</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulkAction('include')}>
                  <Plus className="w-3 h-3 mr-1" /> Add All
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleBulkAction('not_applicable')}>
                  <X className="w-3 h-3 mr-1" /> Skip All
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedItems(new Set())}>
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Back to Dashboard */}
          <div className="bg-white border-b px-4 py-2">
            <button
              onClick={() => setViewMode('dashboard')}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {filteredItems.map((item, idx) => (
                <ListItemCard
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.has(item.id)}
                  onSelect={() => toggleItemSelection(item.id)}
                  onClick={() => handleSelectItem(item)}
                  onQuickAdd={() => handleDecision(item, 'include')}
                  isDecided={isDecided(item.item_key || item.id)}
                  decision={getDecision(item.item_key || item.id)}
                />
              ))}
            </div>
            
            {filteredItems.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Search className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No items match your search</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* DETAIL VIEW */}
      {viewMode === 'detail' && (
        <>
          {/* Back Button */}
          <div className="bg-slate-100 border-b px-4 py-2">
            <button
              onClick={handleBackToList}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to all items
            </button>
          </div>

          {/* Selected Item Header */}
          {selectedJob && (
            <div className={`px-4 py-3 border-b ${
              selectedJob.status === 'diagnostic' ? 'bg-purple-50 border-purple-200' :
              selectedJob.status === 'dangerous' ? 'bg-black/5 border-black' :
              selectedJob.status === 'red' ? 'bg-red-50 border-red-200' :
              selectedJob.status === 'amber' ? 'bg-amber-50 border-amber-200' :
              'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-slate-800">{selectedJob.title}</h4>
                  {selectedJob.notes && (
                    <p className="text-sm text-slate-600 mt-1">{selectedJob.notes}</p>
                  )}
                </div>
                <Badge className={`${
                  selectedJob.status === 'diagnostic' ? 'bg-purple-600' :
                  selectedJob.status === 'dangerous' ? 'bg-black' :
                  selectedJob.status === 'red' ? 'bg-red-600' :
                  selectedJob.status === 'amber' ? 'bg-amber-600' :
                  'bg-slate-600'
                } text-white`}>
                  {selectedJob.status?.toUpperCase()}
                </Badge>
              </div>
              
              {/* Estimated Value */}
              {(selectedJob.estimatedLabour || selectedJob.estimatedParts) && (
                <div className="flex gap-4 mt-2 text-xs text-slate-500">
                  {selectedJob.estimatedLabour > 0 && (
                    <span className="flex items-center gap-1">
                      <Timer className="w-3 h-3" />
                      ~{selectedJob.estimatedLabour}h labour
                    </span>
                  )}
                  {selectedJob.estimatedParts > 0 && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      ~£{selectedJob.estimatedParts} parts
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Completeness Score */}
          {integrityScore && (
            <div className={`px-4 py-3 border-b-2 ${
              integrityScore.risk_level === 'GREEN' ? 'border-green-500' :
              integrityScore.risk_level === 'AMBER' ? 'border-amber-500' :
              'border-red-500'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full ${getScoreColor(integrityScore.risk_level)} flex items-center justify-center`}>
                    <span className="text-white font-bold text-lg">{Math.round(integrityScore.score)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Completeness Score</p>
                    <p className="text-xs text-slate-500">{integrityScore.risk_message}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DIAGNOSTIC PATHWAY PANEL - Shows for warning light items */}
          {selectedJob && (selectedJob.isDiagnostic || 
            selectedJob.item_key?.includes('warning') || 
            selectedJob.item_key?.includes('dash_warning') ||
            selectedJob.item_key?.includes('eml') ||
            selectedJob.notes?.toLowerCase().includes('engine management') ||
            selectedJob.notes?.toLowerCase().includes('eml') ||
            selectedJob.notes?.toLowerCase().includes('check engine')
          ) && (
            <div className="p-4 border-b bg-purple-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">Pathway-Based Diagnostic</span>
                </div>
                <Badge className="bg-purple-600 text-white">Investigation Required</Badge>
              </div>
              {showPathwayPanel ? (
                <DiagnosticPathwayPanel
                  item={selectedJob}
                  yestimateId={estimateId}
                  inspectionId={selectedJob.inspection_id}
                  onJobGroupCreated={(result) => {
                    toast.success(`Created: ${result.job_group_name}`);
                    setShowPathwayPanel(false);
                    // Mark as decided
                    setDecisions(prev => ({
                      ...prev,
                      [selectedJob.item_key || selectedJob.id]: 'include'
                    }));
                    // iter213g — Refresh JCE data (was fetchSuggestions, undefined in this scope).
                    // The panel-level fetchJCE is scoped to the useEffect; caller
                    // should trigger a re-run by bumping a refresh key — no-op safe.
                    // Refresh JCE data — no-op stub (iter213g)
                    // fetchSuggestions();
                  }}
                  onClose={() => setShowPathwayPanel(false)}
                />
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-purple-700">
                    This item requires a diagnostic pathway flow. Choose between Investigation First (default) or Direct Repair (if evidence available).
                  </p>
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowPathwayPanel(true)}
                    data-testid="open-pathway-panel-btn"
                  >
                    <GitBranch className="w-4 h-4 mr-2" />
                    Open Diagnostic Pathway Panel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="p-6 text-center text-slate-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                <p className="text-sm">Loading suggestions...</p>
              </div>
            )}

            {error && (
              <div className="p-6 text-center text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {!loading && !error && jceData && (
              <div className="p-4 space-y-4">
                {/* Apply All Button */}
                {suggestions.length > 0 && (
                  <Button
                    className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                    onClick={handleApplyAll}
                    disabled={applyingAll}
                  >
                    {applyingAll ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCheck className="w-4 h-4 mr-2" />
                    )}
                    Apply All Suggestions ({suggestions.filter(s => !decisions[s.job_key]).length})
                  </Button>
                )}

                {/* Required Items */}
                {requiredItems.length > 0 && (
                  <SuggestionCategory
                    title="Required / Included"
                    icon={<Shield className="w-4 h-4" />}
                    items={requiredItems}
                    priority="required"
                    expanded={expandedCategories.required}
                    onToggle={() => setExpandedCategories(p => ({...p, required: !p.required}))}
                    decisions={decisions}
                    onDecision={handleDecision}
                  />
                )}

                {/* Often Items */}
                {oftenItems.length > 0 && (
                  <SuggestionCategory
                    title="Recommended"
                    icon={<Settings className="w-4 h-4" />}
                    items={oftenItems}
                    priority="often"
                    expanded={expandedCategories.often}
                    onToggle={() => setExpandedCategories(p => ({...p, often: !p.often}))}
                    decisions={decisions}
                    onDecision={handleDecision}
                  />
                )}

                {/* Possible Items */}
                {possibleItems.length > 0 && (
                  <SuggestionCategory
                    title="Worth Considering"
                    icon={<Wrench className="w-4 h-4" />}
                    items={possibleItems}
                    priority="possible"
                    expanded={expandedCategories.possible}
                    onToggle={() => setExpandedCategories(p => ({...p, possible: !p.possible}))}
                    decisions={decisions}
                    onDecision={handleDecision}
                  />
                )}

                {/* No Suggestions */}
                {suggestions.length === 0 && !jceData?.error && (
                  <div className="text-center py-8 text-slate-500">
                    <Check className="w-10 h-10 mx-auto mb-3 text-green-400" />
                    <p className="text-sm font-medium">No additional items suggested</p>
                    <p className="text-xs mt-1">This job appears complete</p>
                  </div>
                )}
              </div>
            )}

            {/* Job not in library */}
            {!loading && !error && jceData?.error && (
              <div className="p-6 text-center text-slate-500">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                <p className="text-sm font-medium">Job not in rule library</p>
                <p className="text-xs mt-1">No predefined suggestions available</p>
              </div>
            )}
          </div>

          {/* Next Item Footer */}
          {nextUnpricedItem && nextUnpricedItem.id !== selectedJob?.id && (
            <div className="p-3 border-t bg-slate-50">
              <Button
                variant="outline"
                className="w-full"
                onClick={goToNextUnpriced}
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Next: {nextUnpricedItem.title}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Panel Footer */}
      <div className="p-2 border-t border-slate-200 bg-slate-50 text-center shrink-0">
        <p className="text-xs text-slate-400">
          Press <kbd className="bg-slate-200 px-1 rounded">?</kbd> for shortcuts
        </p>
      </div>
    </div>
  );
};

/**
 * Quick Item Card for Dashboard
 */
const QuickItemCard = ({ item, onClick, onQuickAdd }) => {
  const statusColors = {
    diagnostic: 'border-purple-300 bg-purple-50',
    dangerous: 'border-black bg-black/5',
    red: 'border-red-200 bg-red-50',
    amber: 'border-amber-200 bg-amber-50',
    priced: 'border-green-200 bg-green-50'
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${statusColors[item.status] || 'border-slate-200 bg-slate-50'}`}>
      <button
        className="flex-1 text-left min-w-0"
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-800 truncate">{item.title}</span>
          {item.isDiagnostic && (
            <Badge className="bg-purple-600 text-white text-xs shrink-0">DIAG</Badge>
          )}
          {item.status === 'dangerous' && !item.isDiagnostic && (
            <Badge className="bg-black text-white text-xs shrink-0">DANGER</Badge>
          )}
        </div>
        {item.notes && (
          <p className="text-xs text-slate-500 truncate mt-0.5">{item.notes}</p>
        )}
        {(item.estimatedLabour || item.estimatedParts) && (
          <p className="text-xs text-slate-400 mt-1">
            {item.estimatedLabour > 0 && `~${item.estimatedLabour}h`}
            {item.estimatedLabour && item.estimatedParts && ' • '}
            {item.estimatedParts > 0 && `~£${item.estimatedParts}`}
          </p>
        )}
      </button>
      <div className="flex gap-1 ml-2 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          onClick={(e) => { e.stopPropagation(); onQuickAdd(); }}
          title="Quick add (A)"
        >
          <Plus className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600"
          onClick={onClick}
          title="View details"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * List Item Card with checkbox
 */
const ListItemCard = ({ item, isSelected, onSelect, onClick, onQuickAdd, isDecided, decision }) => {
  const statusColors = {
    diagnostic: 'border-purple-300 bg-purple-50',
    dangerous: 'border-black bg-black/5',
    red: 'border-red-200 bg-red-50',
    amber: 'border-amber-200 bg-amber-50',
    priced: 'border-green-200 bg-green-50'
  };

  if (isDecided) {
    return (
      <div className="flex items-center p-3 rounded-lg border border-slate-200 bg-slate-50 opacity-60">
        <Check className="w-4 h-4 text-green-600 mr-2" />
        <span className="text-sm text-slate-600 flex-1 truncate">{item.title}</span>
        <Badge variant="secondary" className="text-xs">
          {decision === 'include' ? 'Added' : 'Skipped'}
        </Badge>
      </div>
    );
  }

  return (
    <div className={`flex items-center p-3 rounded-lg border ${statusColors[item.status] || 'border-slate-200 bg-slate-50'} ${isSelected ? 'ring-2 ring-blue-400' : ''}`}>
      <Checkbox
        checked={isSelected}
        onCheckedChange={onSelect}
        className="mr-3"
      />
      <button
        className="flex-1 text-left min-w-0"
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-800 truncate">{item.title}</span>
          {item.isDiagnostic && <Badge className="bg-purple-600 text-white text-xs">DIAG</Badge>}
          {item.status === 'dangerous' && !item.isDiagnostic && <Badge className="bg-black text-white text-xs">DANGER</Badge>}
          {item.isPriced && <Badge className="bg-green-600 text-white text-xs">PRICED</Badge>}
        </div>
        {item.notes && <p className="text-xs text-slate-500 truncate mt-0.5">{item.notes}</p>}
      </button>
      {!item.isPriced && (
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 ml-2"
          onClick={(e) => { e.stopPropagation(); onQuickAdd(); }}
        >
          <Plus className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

/**
 * Suggestion Category Component
 */
const SuggestionCategory = ({ title, icon, items, priority, expanded, onToggle, decisions, onDecision }) => {
  const priorityColors = {
    required: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
    often: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
    possible: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-600' }
  };
  
  const colors = priorityColors[priority];
  const undecidedCount = items.filter(i => !decisions[i.job_key]).length;

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 rounded-lg ${colors.bg} ${colors.border} border`}
      >
        <div className="flex items-center gap-2">
          <span className={colors.text}>{icon}</span>
          <span className={`text-sm font-medium ${colors.text}`}>{title}</span>
          <Badge className={`text-xs ${colors.badge}`}>{undecidedCount}/{items.length}</Badge>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      
      {expanded && (
        <div className="mt-2 space-y-2">
          {items.map(item => (
            <SuggestionItemCard
              key={item.job_key}
              item={item}
              priority={priority}
              isDecided={!!decisions[item.job_key]}
              decision={decisions[item.job_key]}
              onDecision={(type) => onDecision(item, type)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual Suggestion Item Card
 */
const SuggestionItemCard = ({ item, priority, isDecided, decision, onDecision }) => {
  const [showActions, setShowActions] = useState(false);

  const priorityStyles = {
    required: 'border-green-200 bg-green-50/50',
    often: 'border-blue-200 bg-blue-50/50',
    possible: 'border-slate-200 bg-slate-50/50'
  };

  if (isDecided) {
    return (
      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 opacity-60">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">{item.name}</span>
          <Badge variant="secondary" className="text-xs">
            {decision === 'include' ? 'Added' : decision === 'offer' ? 'Will offer' : decision === 'inspect' ? 'Inspect' : 'Skipped'}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-3 ${priorityStyles[priority]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-800">{item.name}</p>
          {item.reason && <p className="text-xs text-slate-600 mt-1">{item.reason}</p>}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            {item.default_hours && (
              <span className="flex items-center gap-1">
                <Timer className="w-3 h-3" />
                {item.default_hours}h
              </span>
            )}
            <span>{item.domain}</span>
          </div>
        </div>
        
        {!showActions ? (
          <Button
            size="sm"
            variant={priority === 'required' ? 'default' : 'outline'}
            className={`shrink-0 ${priority === 'required' ? 'bg-green-600 hover:bg-green-700' : ''}`}
            onClick={() => setShowActions(true)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        ) : (
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="outline" className="px-2 text-green-700 border-green-300 hover:bg-green-50" onClick={() => onDecision('include')} title="Add">
              <Check className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" className="px-2 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => onDecision('offer')} title="Offer">
              <Eye className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" className="px-2 text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => onDecision('inspect')} title="Inspect">
              <Clock className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" className="px-2 text-slate-400" onClick={() => { setShowActions(false); onDecision('not_applicable'); }} title="Skip">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Helper: Get estimated labour hours for an item
 */
function getEstimatedLabour(itemKey) {
  const labourEstimates = {
    'front_brake_pads': 0.8,
    'front_brake_discs': 1.2,
    'front_brake_pads_discs': 1.5,
    'rear_brake_pads': 0.8,
    'rear_brake_discs': 1.2,
    'front_springs': 1.5,
    'rear_springs': 1.5,
    'front_shock_absorbers': 1.5,
    'rear_shock_absorbers': 1.2,
    'warning_lamps': 1.0,
    'dash_warning_lights': 1.0,
    'engine_warning_light': 1.0,
    'drive_belt_condition': 0.5,
    'timing_belt': 4.0,
    'clutch_operation': 6.0
  };
  
  if (!itemKey) return 0;
  
  for (const [key, hours] of Object.entries(labourEstimates)) {
    if (itemKey.includes(key) || key.includes(itemKey)) {
      return hours;
    }
  }
  return 0.5; // Default estimate
}

/**
 * Helper: Get estimated parts cost for an item
 */
function getEstimatedParts(itemKey) {
  const partsEstimates = {
    'front_brake_pads': 45,
    'front_brake_discs': 120,
    'front_brake_pads_discs': 150,
    'rear_brake_pads': 40,
    'rear_brake_discs': 100,
    'front_springs': 80,
    'rear_springs': 70,
    'front_shock_absorbers': 120,
    'rear_shock_absorbers': 100,
    'drive_belt_condition': 35,
    'timing_belt': 200,
    'clutch_operation': 250
  };
  
  if (!itemKey) return 0;
  
  for (const [key, cost] of Object.entries(partsEstimates)) {
    if (itemKey.includes(key) || key.includes(itemKey)) {
      return cost;
    }
  }
  return 0;
}

export default JobCompletenessPanel;
